import { useEffect, useLayoutEffect, useRef, useState } from 'preact/hooks';
import type { OnsiteProduct, OnsitePrice, AuthMode, CampaignPromo, CheckoutText, Credential, CheckoutStep, CheckoutResponse, PromoPreview, TrialStartResponse, CheckoutCompletionResponse, CheckoutSuccessDetails, CheckoutCallbacks, CheckoutResult, CheckoutError, TrialDiscount, ReturnedTrialSetup, ChargedTotals } from '@/types';
import { classifyCampaignCode, clearStashedCampaignPromo } from '@/campaignPromo';
import { getUpgradeOptions, type UpgradeSource, startCheckout, startTrial, confirmTrial, convertToPaid, generateIdempotencyKey, requestMagicLink, establishSession, checkProductAccess, checkItemAccess, isSessionExpired, isRequestRejected, isDomainNotFound, emitFunnelEvent, confirmSubscriptionPayment, type SubscriptionPaymentFailureReason, validatePromoCode, type FunnelEventContext, type FunnelSurface, type IdentityMethod } from '@/api/client';
import { getStripe } from '@/api/stripe-loader';
import { validateAuthAdapter } from '@/authAdapter';
import { getIdentityAdapter } from '@/identityAdapter';
import { isItemScoped, resolveItemForPrice } from '@/page';
import { hasSession, sessionVerified, setPendingIntent, getPendingIntent, clearPendingIntent, clearPendingCheckout, clearSession, beginSessionRequest, type PendingIntent } from '@/session';
import { createRequestScope } from '@/requestScope';
import { emit as emitAccessChange, on as onAccessChange } from '@/accessEvents';
import { markOverlayOpen, markOverlayClosed, allowAds } from '@/adRemoval';
import { acquirePageScrollLock, releasePageScrollLock } from '@/pageScrollLock';
import { logError, logWarn } from '@/sdkConsole';
import { useCheckoutPresentation } from '@/components/CheckoutPresentation';
import { PaywallOverlay } from '@/components/PaywallOverlay';
import { EmailStep } from '@/components/EmailStep';
import { ExpeditedCheckoutStep } from '@/components/ExpeditedCheckoutStep';
import { PaymentStep } from '@/components/PaymentStep';
import { TrialPaymentStep } from '@/components/TrialPaymentStep';
import { TrialIneligibleStep } from '@/components/TrialIneligibleStep';
import { settleTrialCompletion } from '@/components/trialCompletion';
import type { PromoApplyResult } from '@/components/PromoCodeInput';
import { promoReasonMessage, promoDiscountFromValidation, firstChargeFromValidation, trialDiscountFromPromo } from '@/components/promoDescriptor';
import { CheckoutShell } from '@/components/CheckoutShell';
import { UpgradeStep } from '@/components/UpgradeStep';
import { SuccessStep } from '@/components/SuccessStep';
import { LoginStep } from '@/components/LoginStep';
import { ErrorStep } from '@/components/ErrorStep';
import { LocalDevStep } from '@/components/LocalDevStep';
import { isLocalHostname } from '@/localDev';
import { SecureRedirectStep } from '@/components/SecureRedirectStep';
import { checkoutNeedsSecureRedirect, type SecureResumePayload } from '@/secureRedirect';
import { runHook } from '@/components/runHook';
import { t, useT } from '@/i18n/t';
import { dialogAnchorSelector } from '@/dialogAnchor';

// Outcome of a begin* call. 'expired' means the backend rejected the identity
// credential as dead (ErrSessionExpired, 401) and the visitor must
// re-identify; 'failed' is any other start failure; 'abandoned' means the
// selection was torn down (expedited back-to-picker, or a newer selection)
// while the start was in flight, so its result was discarded.
type BeginOutcome = 'ok' | 'failed' | 'expired' | 'abandoned' | 'localdev';

interface Props extends CheckoutCallbacks {
  upgrade?: UpgradeSource;
  upgradeMaskedEmail?: string;
  domainId: number;
  // The domain's auth mode, used to gate checkout: a byo domain with no
  // registered adapter is blocked. Undefined/ezauth is not gated here.
  authMode?: AuthMode;
  // Whether the publisher allows guest checkout; drives the identity gate's
  // "Continue as guest" option. Absent is treated as enabled (backend default).
  guestCheckoutEnabled?: boolean;
  // Whether the publisher opted into expedited checkout: an anonymous visitor
  // skips the identity choices screen and lands on the single-screen guest
  // checkout (email + inline payment). Only ever true alongside guest checkout
  // (the backend enforces the pairing); absent is treated as false.
  expeditedCheckoutEnabled?: boolean;
  // Whether this page's checkout offers wallets (ProductConfig.walletsEnabled).
  // False hides every wallet surface (Express Checkout Element and the Payment
  // Element's own wallet buttons); the session is card-only server-side.
  // Absent is treated as true.
  walletsEnabled?: boolean;
  // Prefills the guest email field (a publisher-supplied showPaywall { email }).
  // Mount-time seed; the visitor's own entries take over once typed.
  initialEmail?: string;
  // Publisher-authored per-domain overrides of the paywall chrome copy, passed
  // through to the paywall's trust line and manage-subscription link.
  checkoutText?: CheckoutText;
  products: OnsiteProduct[];
  // The product key the paywall was opened for, carried into the success result.
  productKey?: string;
  // The mount's campaign code (URL ?promo= stash or the showPaywall promoCode
  // option): the picker shows its discounted prices and the payment step
  // auto-applies it. Null when neither exists.
  campaignPromo?: CampaignPromo | null;
  // The config response's promo block (present when the config request carried
  // the campaign code): per-price discounts for the picker, or the code-level
  // reason. Drives the preview-stage classification (deferred URL strip,
  // console log, foreign-param drop).
  promoPreview?: PromoPreview;
  // The item supplied to showPaywall({ product, item }). Used to resolve the
  // checkout item for a runtime item-scoped price (page-sourced prices derive
  // their item from the current page instead).
  item?: string;
  onUnlock: () => void;
  // When set, the flow can be dismissed at any step (donation, or a dismissible
  // paywall). When unset the paywall stays blocking.
  onDismiss?: () => void;
  // Unmounts the gate when BYO auth moves to the publisher's own surface (their
  // login modal). Wired regardless of dismissible: "the visitor may close this
  // gate" and "the gate must get out of the publisher modal's way" are different
  // concerns — a blocking gate over a publisher modal is the bug this exists to
  // prevent.
  onExternalAuthExit?: () => void;
  // A publisher-requested preset amount (cents) for a variable donation. The
  // picker preselects it when valid for the price; ignored otherwise.
  initialAmountCents?: number;
  // Set when resuming a BYO login or a secure-context redirect with a
  // previously chosen price: the externalId of that price. The flow
  // auto-selects it on mount so the visitor lands past the plan picker. Also
  // set by openCheckout, which preselects the publisher-chosen price.
  initialPriceExternalId?: string;
  // Set when the resumed selection was a trial start, so resume routes through
  // the trial path rather than a paid checkout.
  initialTrial?: boolean;
  // Set when the page is Stripe's return from a trial card check's 3DS
  // redirect: the trial confirms on this already-confirmed SetupIntent instead
  // of starting a fresh card check, with the flow's credential or, when there
  // is none, the return's client secret.
  trialSetupResume?: ReturnedTrialSetup;
  // Fired when the flow takes trialSetupResume for its confirm, so the sdk
  // stops merging it into later mounts on this load.
  onTrialSetupResumeConsumed?: () => void;
  // Funnel surface override. When set (openCheckout passes 'checkout'), it wins
  // over the product-type derivation below; absent leaves the derivation
  // (paywall/donation) intact.
  surface?: FunnelSurface;
  // True when this mount re-opens a previously counted selection (BYO login
  // return, http→https resume): the auto-select skips re-emitting
  // price_selected. A fresh openCheckout mount (resumed undefined/false) emits
  // it, since the publisher's own UI made a real selection.
  resumed?: boolean;
}

// selectionCredential picks the logged-in skip's credential when no verified
// session exists. A soft session (unproven email) isn't trusted for saved
// cards, so an ezAuth or identity token the mode adapter can prove wins over
// it; an email-only adapter adds nothing, so the soft session stays.
function selectionCredential(session: Credential | null, adapterCredential: Credential | null): Credential | null {
  if (!session) {
    return adapterCredential;
  }
  if (adapterCredential && ('ezAuthToken' in adapterCredential || 'identityToken' in adapterCredential)) {
    return adapterCredential;
  }
  return session;
}

export function CheckoutFlow({ upgrade: initialUpgrade, upgradeMaskedEmail: initialUpgradeMaskedEmail, domainId, authMode, guestCheckoutEnabled, expeditedCheckoutEnabled, walletsEnabled, initialEmail, checkoutText, products, productKey, item, campaignPromo, promoPreview, onUnlock, onDismiss, onExternalAuthExit, onSuccess, onCancel, onError, initialAmountCents, initialPriceExternalId, initialTrial, trialSetupResume, onTrialSetupResumeConsumed, surface: surfaceOverride, resumed }: Props) {
  useT();
  const [upgrade, setUpgrade] = useState(initialUpgrade);
  const [upgradeMaskedEmail, setUpgradeMaskedEmail] = useState(initialUpgradeMaskedEmail);
  const [identityVersion, setIdentityVersion] = useState(0);
  const [switchingIdentity, setSwitchingIdentity] = useState(false);
  const switchingIdentityRef = useRef(false);
  const switchedFromUpgrade = useRef(false);
  const awaitingExternalIdentity = useRef(false);
  const [identityRequests] = useState(() => createRequestScope());
  const flowClosed = useRef(false);
  const flowLive = () => !flowClosed.current && (!isActive || isActive());
  const invalidateIdentity = () => {
    identityRequests.advance();
    startRequests.advance();
  };
  useEffect(() => () => {
    flowClosed.current = true;
    invalidateIdentity();
  }, []);
  const [upgradeHasAccess, setUpgradeHasAccess] = useState(false);
  const upgradeChargedRef = useRef<number | undefined>();
  // The upgrade's target, set at success: a resumed upgrade never selected
  // it through state, and finishSuccess runs before a state update lands.
  const upgradeTargetRef = useRef<{ product: OnsiteProduct; price: OnsitePrice } | undefined>();
  const { inline, successUrl, isActive } = useCheckoutPresentation();
  const [step, setStep] = useState<CheckoutStep>('idle');
  // Once a checkout succeeds, dismissing the success screen is a close, not a
  // cancel, so onCancel must not fire afterward.
  const [succeeded, setSucceeded] = useState(false);
  // Seed the selection from a preselected price (openCheckout, or a resume) so
  // the mount-time funnelContext (modal_shown) already carries it — the
  // auto-select effect runs after the first render, too late for that emit.
  const [selectedProduct, setSelectedProduct] = useState<OnsiteProduct | null>(() => {
    if (!initialPriceExternalId) return null;
    return products.find((p) => p.prices.some((pr) => pr.externalId === initialPriceExternalId)) ?? null;
  });
  const [selectedPrice, setSelectedPrice] = useState<OnsitePrice | null>(() => {
    if (!initialPriceExternalId) return null;
    for (const p of products) {
      const found = p.prices.find((pr) => pr.externalId === initialPriceExternalId);
      if (found) return found;
    }
    return null;
  });
  // Tracks whether the preselected-price auto-select effect has run. Until it
  // has, the 'idle' step renders nothing (no picker flash on a preselected
  // mount). After it runs, 'idle' renders the picker normally — Back buttons,
  // error retry, and a resume whose price vanished all land there, so the
  // picker must stay reachable for the mount's lifetime, not just at mount.
  const [autoSelectDone, setAutoSelectDone] = useState(!initialPriceExternalId);
  // The visitor's chosen amount for a variable donation; null for fixed prices.
  const [selectedAmountCents, setSelectedAmountCents] = useState<number | null>(null);
  const [checkoutData, setCheckoutData] = useState<CheckoutResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [loginLinkSent, setLoginLinkSent] = useState(false);
  // The email the passwordless-first login screen collected, carried into the
  // magic-link step for prefill.
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  // Set when a returning member who logged in at the gate already held the
  // entitlement, so the success screen confirms access instead of a purchase.
  const [alreadyEntitled, setAlreadyEntitled] = useState(false);
  // The email a guest checked out with (the EmailStep guest path, not a logged-in
  // credential). Drives the success screen's optional guest → account conversion;
  // null for a logged-in buyer.
  const [guestEmail, setGuestEmail] = useState<string | null>(null);
  // Mirrors guestEmail for handlers that settle in the same tick the email
  // submit set it (a 3DS-return resume confirms right off the submit).
  const guestEmailRef = useRef<string | null>(null);
  const adoptGuestEmail = (email: string | null) => {
    guestEmailRef.current = email;
    setGuestEmail(email);
  };
  // The guest → account conversion offer's data: the masked email to display and
  // the attestation to present to sol's register. Set when the buyer is a guest
  // (guestEmail set this session, or a deferred/expedited anonymous start) and
  // the completion carried an attestation; null for a logged-in buyer.
  const [conversion, setConversion] = useState<{ maskedEmail: string; emailAttestation: string } | null>(null);
  // How the current payment/trialPayment step was reached: 'guest' via the
  // EmailStep guest form, 'authenticated' via a gate login/register or the
  // logged-in skip that never showed the gate. Drives where Back from payment
  // lands (handlePaymentBack).
  const [paymentBackTarget, setPaymentBackTarget] = useState<'guest' | 'authenticated' | null>(null);
  // Trial flow state. trialMode marks the current selection as a trial (drives
  // which begin*/finalize path the email and gate handlers take); trialData is
  // the SetupIntent the trial payment step mounts; trialCredential is the
  // identity StartTrial resolved against, replayed verbatim on confirm/convert;
  // pendingSetupIntentId carries the confirmed card into the convert-to-paid ack;
  // succeededAsTrial selects the success copy (trial vs paid). trialCredential
  // is a ref: the deferred path's confirm-time Gate-1 handler reads it in the
  // same tick handleConfirmTrial adopts it, before any re-render.
  const [trialMode, setTrialMode] = useState(false);
  const [trialData, setTrialData] = useState<TrialStartResponse | null>(null);
  const trialCredentialRef = useRef<Credential | null>(null);
  // The 3DS-return proof a credential-less resumed confirm succeeded with, so
  // a convert-to-paid on that SetupIntent identifies the buyer the same way.
  const trialProofRef = useRef<{ setupIntentId: string; secret: string } | null>(null);
  const [pendingSetupIntentId, setPendingSetupIntentId] = useState<string | null>(null);
  // Pinned when the convert-to-paid ack opens so a retried Subscribe click
  // replays the same Stripe create (idempotency) instead of minting a second
  // incomplete subscription.
  const convertIdempotencyKeyRef = useRef<string | null>(null);
  // The promo code the denying trial form hands up at the Gate-2 handoff,
  // pinned alongside the idempotency key. Captured from the form (not the
  // flow-level promoCodeRef) so a back-out-and-switch-plans sequence can't
  // send a code applied to a different product.
  const convertPromoCodeRef = useRef<string | null>(null);
  // The same handoff's code + validated discount + first-charge preview as
  // render state: the acknowledgment shows the carried promo (chip + sidebar
  // breakdown) so it never states the full price for a charge the convert will
  // discount.
  const [ineligiblePromo, setIneligiblePromo] = useState<{ code: string; discount: TrialDiscount | null; firstCharge: ChargedTotals | null } | null>(null);
  // convertCreated: a convert has created the subscription, so the ack's promo
  // is locked (Stripe counted it at create, and a changed promo under the same
  // idempotency key fails the replay). convertFailed: its payment failed, so
  // the ack offers a new payment method.
  const [convertCreated, setConvertCreated] = useState(false);
  const [convertFailed, setConvertFailed] = useState(false);
  const [succeededAsTrial, setSucceededAsTrial] = useState(false);
  // What the success screen renders beyond the completion: the paid path's
  // final session (actual charged totals) or the trial/convert path's applied
  // promo (chip + sidebar discount note).
  const [successDetails, setSuccessDetails] = useState<CheckoutSuccessDetails | null>(null);
  // The success screen's one-click portal link when it arrives only at
  // completion: identified checkouts get it on the start response
  // (checkoutData/trialData), but a deferred-identity checkout (Stripe collects
  // the email, so the visitor is created at completion) gets it only on the
  // completion. Without adopting it here the success link falls back to the
  // bare portal and the buyer is not signed in.
  const [completionPortalUrl, setCompletionPortalUrl] = useState<string | null>(null);
  // The promo code the visitor applied on the payment step or the
  // trial-ineligible acknowledgment (null when none). A ref: only read when
  // emitting funnel events, so applying a code shouldn't re-render the flow.
  const promoCodeRef = useRef<string | null>(null);
  // Set when the campaign code is dropped for this mount: a not_found
  // classification (foreign ?promo= param) or the visitor removing it at the
  // payment step. Suppresses the picker's discounts and the auto-apply.
  const [campaignDropped, setCampaignDropped] = useState(false);
  const activeCampaignPromo = campaignDropped ? null : (campaignPromo ?? null);

  // Classify the campaign code at preview: the config's promo block carries
  // the code-level reason (or the discounts), driving the deferred URL strip,
  // the console log, and the foreign-param drop. Once per mount.
  const campaignClassifiedRef = useRef(false);
  useEffect(() => {
    if (campaignClassifiedRef.current || !campaignPromo || !promoPreview) {
      return;
    }
    campaignClassifiedRef.current = true;
    classifyCampaignCode(campaignPromo.source, promoPreview.reason ?? null, 'preview');
    if (promoPreview.reason === 'not_found') {
      setCampaignDropped(true);
    }
  }, [campaignPromo, promoPreview]);

  // handlePromoChange is the steps' applied-code reporter. A removal of the
  // campaign code dismisses it for the rest of the mount (manual wins after
  // mount) and clears the URL stash.
  const handlePromoChange = (code: string | null) => {
    const removed = code === null ? promoCodeRef.current : null;
    promoCodeRef.current = code;
    if (removed && campaignPromo && removed.toUpperCase() === campaignPromo.code) {
      setCampaignDropped(true);
      if (campaignPromo.source === 'url') {
        clearStashedCampaignPromo();
      }
    }
  };

  // Beacons a successful campaign auto-apply (success-only by contract).
  const handleCampaignApply = () => {
    if (!campaignPromo) {
      return;
    }
    emitFunnelEvent('promo_campaign_apply', { ...funnelContext(), promoCode: campaignPromo.code });
  };

  // Beacons a failed campaign auto-apply. errorCode carries the most specific
  // diagnostic available: the validate reason, or Stripe's raw error text when
  // EWCS rejected the apply (its code is null for everything but invalidCode),
  // falling back to bare apply_failed. promo_campaign_apply stays success-only;
  // this event is diagnostic-only — the funnel panel does not chart it.
  const handleCampaignApplyFailed = (reason: string, detail?: string) => {
    if (!campaignPromo) {
      return;
    }
    emitFunnelEvent('promo_campaign_apply_failed', { ...funnelContext(), promoCode: campaignPromo.code, errorCode: detail || reason });
  };
  // First-selection-wins guard for identity_method_selected: the visitor's
  // initial gate choice is the one that counts for the funnel; later
  // re-selections within this flow mount (payment → Back, method switching)
  // do not re-emit. This component mounts only while the modal is on screen,
  // so the ref's lifetime matches the modal_shown funnel instance exactly.
  const identityMethodRecordedRef = useRef(false);

  // Records the visitor's identity disposition, first one per mount only.
  // Passed to the checkout-intent EmailStep (standalone login surfaces — the
  // 'login' step — get no callback and never record) and called directly on
  // the authed-skip path with an explicit context, since the selecting
  // handler's funnelContext closure still sees stale pre-selection state.
  const recordIdentityMethod = (method: IdentityMethod, context?: FunnelEventContext) => {
    if (identityMethodRecordedRef.current) {
      return;
    }
    identityMethodRecordedRef.current = true;
    emitFunnelEvent('identity_method_selected', { ...(context ?? funnelContext()), identityMethod: method });
  };

  // The amount the summaries should show for a donation: the chosen variable
  // amount when present, else the fixed catalog price.
  const donationAmountCents = selectedProduct?.type === 'donation'
    ? selectedAmountCents ?? selectedPrice?.amountCents
    : undefined;

  // This component backs three funnel surfaces: the paywall (auto-mounted or
  // showPaywall), the donation dialog (DonationLauncher passes donation
  // products here), and direct checkout (openCheckout passes the 'checkout'
  // override). Derived from the product type so the paywall/donation entry
  // points need no prop; an explicit override (openCheckout) wins, since its
  // products aren't donations and would otherwise mislabel as 'paywall'.
  const surface: FunnelSurface = surfaceOverride ?? (products.some((p) => p.type === 'donation') ? 'donation' : 'paywall');
  // A direct openCheckout has no plan to pick — the publisher already chose the
  // price — so it never shows the plan picker and offers no Back-to-picker
  // navigation. The paywall and donation surfaces keep the picker and its Back.
  const canGoBackToPicker = surface !== 'checkout';

  // The funnel context every stage event in this flow carries. Selected
  // product/price are null until the plan picker resolves, so early events
  // (modal_shown) carry the flow's first product only. The price key lets the
  // backend re-stamp amount/currency/interval/trial_days from the catalog
  // rather than trusting these client hints.
  const funnelContext = () => ({
    surface,
    flow: expeditedActive ? ('expedited' as const) : ('classic' as const),
    productExternalId: selectedProduct?.externalId ?? products[0]?.externalId,
    priceKey: selectedPrice?.priceKey,
    priceExternalId: selectedPrice?.externalId,
    amountCents: donationAmountCents ?? selectedPrice?.amountCents,
    currency: selectedPrice?.currency,
    billingInterval: selectedPrice?.billingInterval,
    promoCode: promoCodeRef.current ?? undefined,
    // The item the checkout will unlock, once a price is selected to resolve
    // it against (item-scoped prices only; product-scoped never carry one).
    item: selectedPrice ? resolveItemForPrice(selectedPrice, item) : undefined,
  });

  // The same context built from explicit selection args. Required inside
  // handleSelectPrice/handleStartTrial: those handlers set the selected state
  // and then emit in the same tick, where the funnelContext closure still sees
  // the pre-selection nulls (Preact state applies on the next render).
  const selectionContext = (product: OnsiteProduct, price: OnsitePrice, amountCents?: number | null): FunnelEventContext => ({
    surface,
    flow: expeditedActive ? 'expedited' : 'classic',
    productExternalId: product.externalId,
    priceKey: price.priceKey,
    priceExternalId: price.externalId,
    amountCents: product.type === 'donation' ? amountCents ?? price.amountCents : price.amountCents,
    currency: price.currency,
    billingInterval: price.billingInterval,
    item: resolveItemForPrice(price, item),
  });

  // Single guarded entry point for the publisher's onError hook, shared by the
  // checkout-start failure here and the payment-step failures below.
  const reportError = (error: CheckoutError) => {
    if (!flowLive()) {
      return;
    }
    runHook(onError, 'onError', error);
  };

  // Expedited checkout: an anonymous visitor skips the identity choices screen
  // and lands on the single-screen guest checkout (ExpeditedCheckoutStep at the
  // 'email' step), which starts the checkout anonymously at mount and collects
  // email + payment via Stripe's own elements. Guest-only by construction (the
  // backend pairs the flags). A trial selection uses the same single screen,
  // mounting the trial card-setup form inline off trialData.
  const expedited = expeditedCheckoutEnabled === true && guestCheckoutEnabled !== false;
  // Set when the expedited screen's anonymous mount-time start failed (rate
  // limit, Stripe blip): the flow degrades to the standard non-expedited
  // email-first path for the rest of this modal's lifetime. Quiet by design —
  // the visitor did nothing wrong, so no inline error and no publisher onError.
  const [expeditedStartFailed, setExpeditedStartFailed] = useState(false);
  // The 3DS-return resume, taken by the first trial start for the resumed
  // price: with a credential, or with its client secret when there is none
  // (resumeTrialWithProof). Dropped once the server settles it (any result, or
  // a definitive 4xx refusal of a credential confirm). While pending, expedited
  // is off: its anonymous mount-time start would mint a fresh SetupIntent,
  // while the email step collects the identity a refused proof still needs.
  const resumeTrialRef = useRef(trialSetupResume ?? null);
  // True while a resumed confirm (and any fallback start) is in flight: the
  // flow shows the trial's starting view instead of the step behind it.
  const [resumingTrial, setResumingTrial] = useState(false);
  // The trial park this flow owns (its own card check, or the resume it was
  // mounted with): exit paths clear the stored park only when it is this one.
  const ownTrialParkRef = useRef<string | null>(trialSetupResume?.setupIntentId ?? null);
  // The resumed trial's Stripe init: a resume has no trialData (it never ran
  // startTrial), but a first invoice or convert that needs 3DS loads Stripe.js.
  const resumedTrialStripeRef = useRef<{ publishableKey: string; connectedAccount?: string } | null>(null);
  const trialStripeKeys = () => (trialData ? { publishableKey: trialData.publishableKey, connectedAccount: trialData.providerAccount } : resumedTrialStripeRef.current);
  const loadTrialStripe = async () => {
    const keys = trialStripeKeys();
    if (!keys) {
      return null;
    }
    return getStripe(keys.publishableKey, keys.connectedAccount);
  };
  const expeditedActive = expedited && !expeditedStartFailed && !switchedFromUpgrade.current && !resumeTrialRef.current && !resumingTrial;
  // Marks that this flow's checkout ran deferred (anonymous mount-time start on
  // the expedited screen): the widget never held the buyer's email, so the
  // success screen's account offer reads it off the completion payload.
  const deferredIdentityRef = useRef(false);
  // Runs an anonymous expedited start; a failure falls back quietly to the
  // email-first flow. Resolves false when the start failed.
  const runExpeditedStart = async (start: () => Promise<BeginOutcome>): Promise<boolean> => {
    deferredIdentityRef.current = true;
    const outcome = await start();
    if (outcome === 'failed' || outcome === 'expired') {
      deferredIdentityRef.current = false;
      setExpeditedStartFailed(true);
      return false;
    }
    return true;
  };
  // Set when this flow's checkout ran on a content session: resolves true when
  // the mode adapter found no site login (no ezAuth token, no BYO identity), so
  // the buyer may be an account-less returning guest and the success screen
  // offers account creation. A session minted from the site login itself (BYO
  // authChanged, ezAuth restore) resolves false. Resets set null, which also
  // discards a lookup still in flight.
  const sessionGuestRef = useRef<Promise<boolean> | null>(null);
  // Guards beginCheckout/beginTrial against applying a start whose selection
  // is gone: the expedited back-to-picker (or a newer pick) bumps it, and a
  // start that resolves after the bump returns 'abandoned' without touching
  // step or session state.
  const [startRequests] = useState(() => createRequestScope());

  // Shared payment-attempt failure handler for the payment step and the
  // expedited single screen. Only a real Stripe.js failure carries an error
  // code; post-confirm backend-completion failures (access-provisioning
  // delay/timeout) reach onError without one, and the payment actually
  // succeeded — emitting payment_failed there would pair a failure with the
  // checkout_completed that lands moments later, inflating the decline stage
  // with non-declines.
  const handlePaymentError = (paymentError: CheckoutError) => {
    if (paymentError.code) {
      emitFunnelEvent('payment_failed', { ...funnelContext(), errorCode: paymentError.code, paymentMethod: paymentError.paymentMethod });
    }
    reportError(paymentError);
  };

  // A visitor already holding a content session checks out on that session: the
  // backend resolves their identity from the bearer token, so they never re-enter
  // an email or re-authenticate via ezAuth to buy another product. Returns null
  // for an anonymous visitor, so the flow falls back to the mode adapter's
  // credential and then the guest EmailStep. A soft session yields to a provable
  // adapter token (selectionCredential).
  const sessionCredential = (): Credential | null => (hasSession() ? { session: true } : null);

  // reIdentify routes an expired credential back to the EmailStep. A dead SM
  // content session was already dropped by the start request that sent it; a
  // dead ezAuth token has no client-side residue (it's revoked server-side) —
  // the EmailStep's login form just reflects the logged-out state. begin*
  // already set the expiry message, which the EmailStep renders inline.
  const reIdentify = () => {
    // The credential behind any in-flight checkout is dead; drop its data so
    // the plan picker doesn't keep showing the stale identity note.
    setCheckoutData(null);
    setCompletionPortalUrl(null);
    setTrialData(null);
    sessionGuestRef.current = null;
    setStep('email');
  };

  // "Not you?" — full identity teardown, then the gate. Local teardown runs
  // first so a slow ezAuth revoke (or a BYO navigate-away) never delays the
  // gate; mirrors the sdk.ts logout() teardown. The abandoned Stripe session
  // expires on its own.
  const handleSwitchIdentity = async (product = selectedProduct, price = selectedPrice) => {
    const logout = getIdentityAdapter(authMode, domainId).logout;
    if (!logout || switchingIdentityRef.current || !flowLive()) {
      return;
    }
    switchingIdentityRef.current = true;
    switchedFromUpgrade.current ||= !!upgrade;
    invalidateIdentity();
    const identityCurrent = identityRequests.capture();
    setSwitchingIdentity(true);
    setSelectedProduct(product);
    setSelectedPrice(price);
    setUpgrade(undefined);
    setUpgradeMaskedEmail(undefined);
    setUpgradeHasAccess(false);
    setIdentityVersion((value) => value + 1);
    upgradeTargetRef.current = undefined;
    upgradeChargedRef.current = undefined;
    setSucceeded(false);
    setAlreadyEntitled(false);
    setSuccessDetails(null);
    setConversion(null);
    setPendingSetupIntentId(null);
    convertIdempotencyKeyRef.current = null;
    convertPromoCodeRef.current = null;
    setIneligiblePromo(null);
    setConvertCreated(false);
    setConvertFailed(false);
    setSucceededAsTrial(false);
    setMagicLinkEmail('');
    setLoginLinkSent(false);
    promoCodeRef.current = null;
    if (switchedFromUpgrade.current) {
      setTrialMode((price?.trialPeriodDays ?? 0) > 0);
    }
    clearSession();
    clearOwnPendingIntent();
    clearPendingCheckout();
    setCheckoutData(null);
    setCompletionPortalUrl(null);
    setTrialData(null);
    trialCredentialRef.current = null;
    trialProofRef.current = null;
    resumedTrialStripeRef.current = null;
    adoptGuestEmail(null);
    setPaymentBackTarget(null);
    setError(null);
    deferredIdentityRef.current = false;
    sessionGuestRef.current = null;
    allowAds();
    emitAccessChange();
    setStep(price ? 'email' : 'login');
    try {
      await logout();
    } catch (err) {
      if (flowLive() && identityCurrent()) {
        const message = t('checkout.errorGeneric');
        setError(message);
        reportError({ message });
        setStep('error');
      }
      logError('checkout identity logout failed', err);
      return;
    } finally {
      if (flowLive() && identityCurrent()) {
        switchingIdentityRef.current = false;
        setSwitchingIdentity(false);
      }
    }
    if (!flowLive() || !identityCurrent()) {
      return;
    }
    if (switchedFromUpgrade.current) {
      clearSession();
      return;
    }
    // Expedited: the 'email' step is the single screen, which skeletons until
    // a session lands — and the anonymous start only fires from the selection
    // handlers, which already ran. Re-fire it against the surviving selection
    // (the visitor is now exactly the expedited target: anonymous with a
    // price chosen). Same quiet degradation as the mount-time start.
    if (expeditedActive && selectedProduct && selectedPrice) {
      void runExpeditedStart(() => (trialMode
        ? beginTrial(selectedProduct, selectedPrice, null, 'email', { quiet: true })
        : beginCheckout(selectedProduct, selectedPrice, selectedAmountCents, null, 'email', { quiet: true })));
    }
  };

  // The switch is offered only where an identity note can render (a masked
  // email is showing), the mode can actually sign out (ezAuth always, BYO
  // only when the publisher registered a logout), and the identity is a real
  // site identity — a guest who typed their own email at the gate gets the
  // note but no switch: there is nothing to tear down, and Back already
  // reopens the email form. The adapter lookup stays behind the masked-email
  // check so an anonymous deferred checkout never consults the mode adapter.
  const maskedIdentity = upgrade ? upgradeMaskedEmail : checkoutData?.maskedEmail ?? trialData?.maskedEmail;
  const onSwitchIdentity = maskedIdentity && paymentBackTarget !== 'guest' && getIdentityAdapter(authMode, domainId).logout ? () => { void handleSwitchIdentity(); } : undefined;

  const handleSelectPrice = async (product: OnsiteProduct, price: OnsitePrice, amountCents?: number) => {
    const selectionIdentity = identityRequests.capture();
    // A normal (non-trial) selection clears any trial intent left over from a
    // previous trial CTA in this session, so the email/gate handlers don't
    // route the new price down the trial path. The trial start state goes too:
    // the expedited single screen reads trialData, so a Gate-2 back-out's
    // abandoned SetupIntent must not remount against the new selection.
    setTrialMode(false);
    setTrialData(null);
    trialCredentialRef.current = null;
    trialProofRef.current = null;
    setSelectedProduct(product);
    setSelectedPrice(price);
    setSelectedAmountCents(amountCents ?? null);
    setError(null);

    // A page loaded over plain http can never mount Stripe.js in live mode, so
    // park the selection and route through the secure-redirect interstitial
    // before any identity work — the https reload resumes from it. Test keys
    // tolerate http, so a sandbox backend runs checkout in place.
    const needsSecureRedirect = await checkoutNeedsSecureRedirect();
    if (!flowLive() || !selectionIdentity()) {
      return;
    }
    if (needsSecureRedirect) {
      setStep('secureRedirect');
      return;
    }

    // A byo domain must have a registered adapter before checkout can run;
    // validateAuthAdapter logs why when it blocks.
    if (!validateAuthAdapter(authMode)) {
      if (inline) {
        const message = t('inline.configFailed.permanent');
        setError(message);
        reportError({ message });
        setStep('error');
      }
      return;
    }

    // A logged-in visitor (content session, BYO email, or ezAuth token) skips the
    // guest EmailStep; an anonymous visitor collects an email as before. A
    // verified session routes on its own, so its adapter lookup only feeds the
    // conversion offer (sessionGuestRef) and stays off the path to payment.
    const session = sessionCredential();
    const verified = session !== null && sessionVerified();
    const adapterLookup = getIdentityAdapter(authMode, domainId).resolveCredential();
    const credential = verified ? session : selectionCredential(session, await adapterLookup);
    if (!flowLive() || !selectionIdentity()) {
      return;
    }
    if (credential) {
      setPaymentBackTarget('authenticated');
      // A logged-in skip supersedes an abandoned guest attempt (typed gate
      // email, deferred anonymous start) left behind by a back-out to the
      // picker: this checkout is identified, so those markers must not resurrect
      // the account offer on its success screen. Same reset as handleAuthenticated.
      adoptGuestEmail(null);
      deferredIdentityRef.current = false;
      sessionGuestRef.current = 'session' in credential ? adapterLookup.then((adapterCredential) => adapterCredential === null) : null;
      // No gate action happened, but record the skip so the authed cohort is a
      // labeled bucket rather than an invisible identity→payment gap. The
      // per-mount guard still applies: an expired credential that routes back
      // to the gate does not produce a second event on re-selection.
      recordIdentityMethod('existing_session', selectionContext(product, price, amountCents ?? null));
      // Logged-in skip: an expired credential routes back to the EmailStep to
      // re-identify; any other failure has no email form to fall back to, so it
      // surfaces on the dedicated error step instead of closing silently.
      const outcome = await beginCheckout(product, price, amountCents ?? null, credential);
      if (outcome === 'expired') {
        reIdentify();
        return;
      }
      if (outcome === 'failed') {
        setStep('error');
      }
      return;
    }
    // A fresh anonymous selection opens the gate on its choices, even if an
    // earlier back-from-payment left paymentBackTarget at 'guest'.
    setPaymentBackTarget(null);
    // Expedited: start the checkout anonymously right now (the deferred-
    // identity start) so the single screen renders the Stripe elements
    // immediately — wallets, Stripe's email element, then the card form. A
    // mount-time start failure degrades quietly to the standard email-first
    // flow (no inline error, no publisher onError: the visitor did nothing).
    if (expeditedActive) {
      setStep('email');
      await runExpeditedStart(() => beginCheckout(product, price, amountCents ?? null, null, 'email', { quiet: true }));
      return;
    }
    setStep('email');
  };

  // Trial CTA on the paywall. Same identity gating as handleSelectPrice — a
  // logged-in visitor skips the email step — but routes to the trial path. The
  // gate, email submit, and resume handlers branch on trialMode from here.
  const handleStartTrial = async (product: OnsiteProduct, price: OnsitePrice) => {
    const selectionIdentity = identityRequests.capture();
    setTrialMode(true);
    // Clear a previous trial's start state (e.g. a Gate-2 back-out): the
    // expedited single screen reads trialData, so the abandoned SetupIntent
    // must not remount against this new selection.
    setTrialData(null);
    trialCredentialRef.current = null;
    trialProofRef.current = null;
    setSelectedProduct(product);
    setSelectedPrice(price);
    setSelectedAmountCents(null);
    setError(null);

    // Same insecure-context gate as handleSelectPrice (see there).
    const needsSecureRedirect = await checkoutNeedsSecureRedirect();
    if (!flowLive() || !selectionIdentity()) {
      return;
    }
    if (needsSecureRedirect) {
      setStep('secureRedirect');
      return;
    }

    if (!validateAuthAdapter(authMode)) {
      if (inline) {
        const message = t('inline.configFailed.permanent');
        setError(message);
        reportError({ message });
        setStep('error');
      }
      return;
    }

    // Same credential choice as handleSelectPrice (see there).
    const session = sessionCredential();
    const verified = session !== null && sessionVerified();
    const adapterLookup = getIdentityAdapter(authMode, domainId).resolveCredential();
    const credential = verified ? session : selectionCredential(session, await adapterLookup);
    if (!flowLive() || !selectionIdentity()) {
      return;
    }
    if (credential) {
      setPaymentBackTarget('authenticated');
      // Same abandoned-guest reset as handleSelectPrice (see there).
      adoptGuestEmail(null);
      deferredIdentityRef.current = false;
      sessionGuestRef.current = 'session' in credential ? adapterLookup.then((adapterCredential) => adapterCredential === null) : null;
      // Same authed-skip recording as handleSelectPrice (see there).
      recordIdentityMethod('existing_session', selectionContext(product, price));
      // Same routing as handleSelectPrice's logged-in skip (see there).
      const outcome = await beginTrial(product, price, credential);
      if (outcome === 'expired') {
        reIdentify();
        return;
      }
      if (outcome === 'failed') {
        setStep('error');
      }
      return;
    }
    if (await resumeTrialWithProof(product, price)) {
      return;
    }
    if (!flowLive() || !selectionIdentity()) {
      return;
    }
    // Same fresh-gate reset as handleSelectPrice's anonymous path (see there).
    setPaymentBackTarget(null);
    // Expedited: the deferred trial start mints the SetupIntent at mount so
    // the single screen renders the wallet buttons + email element + card
    // form immediately. Same quiet degradation as the paid path (see there).
    if (expeditedActive) {
      setStep('email');
      await runExpeditedStart(() => beginTrial(product, price, null, 'email', { quiet: true }));
      return;
    }
    setStep('email');
  };

  // The price-selection stage: a price is now committed to this flow. For the
  // paywall/donation picker that is the visitor's click; for a preselected
  // openCheckout mount the pick happened on the publisher's own pricing UI and
  // the auto-select effect emits it (fresh mount only). A resume auto-select
  // (resumed=true) replays a selection already counted before the login or
  // https redirect, so it does not re-emit.
  const emitPriceSelected = (product: OnsiteProduct, price: OnsitePrice, amountCents?: number) => {
    emitFunnelEvent('price_selected', {
      surface,
      flow: expeditedActive ? 'expedited' : 'classic',
      productExternalId: product.externalId,
      priceKey: price.priceKey,
      priceExternalId: price.externalId,
      amountCents: amountCents ?? price.amountCents,
      currency: price.currency,
      billingInterval: price.billingInterval,
      item: resolveItemForPrice(price, item),
    });
  };

  const pickPrice = (product: OnsiteProduct, price: OnsitePrice, amountCents?: number) => {
    emitPriceSelected(product, price, amountCents);
    void handleSelectPrice(product, price, amountCents);
  };

  const pickTrial = (product: OnsiteProduct, price: OnsitePrice) => {
    emitPriceSelected(product, price);
    void handleStartTrial(product, price);
  };

  // Track this surface's lifetime for ad removal: while it is on screen, a
  // disableAds() triggered by checkout success defers its reload so the success
  // screen is not destroyed mid-view; the reload fires when this unmounts (the
  // visitor dismisses or clicks Continue, which unmounts the flow via the parent).
  useEffect(() => {
    if (inline) {
      return;
    }
    markOverlayOpen();
    return () => markOverlayClosed();
  }, []);

  // Top of this surface's funnel: the dialog rendered. CheckoutFlow only
  // mounts when the modal actually shows (Paywall gates rendering on the
  // access check; DonationLauncher returns null until opened), and the parents
  // unmount before re-showing, so mount === one real showing.
  useEffect(() => {
    if (!upgrade) {
      emitFunnelEvent('modal_shown', funnelContext());
    }
  }, []);

  // Lock the host page's scroll for the flow's lifetime so the page behind the
  // fixed overlay can't scroll. Ref-counted (see pageScrollLock), so it stays
  // locked across the gate -> checkout steps (one instance) and restores on
  // unmount — after any success screen is dismissed, not on success itself.
  useLayoutEffect(() => {
    if (inline) {
      return;
    }
    acquirePageScrollLock();
    return releasePageScrollLock;
  }, []);

  // Preselected-price entry: a resume (BYO login, secure-context redirect) or a
  // direct openCheckout auto-selects the price so the visitor advances straight
  // past the plan picker. Runs once on mount; a price that no longer exists
  // falls through to the plan picker. A fresh openCheckout (resumed falsy)
  // counts the selection — the publisher's own UI made a real pick — so it
  // emits price_selected; a resume replays an already-counted selection and
  // skips the emit.
  useEffect(() => {
    if (upgrade || !initialPriceExternalId) {
      return;
    }
    // Mark the auto-select run whatever the outcome, so a post-mount 'idle'
    // (Back, retry, a vanished price) renders the picker instead of a blank
    // modal. A price that no longer exists falls through to the picker.
    setAutoSelectDone(true);
    for (const product of products) {
      const price = product.prices.find((p) => p.externalId === initialPriceExternalId);
      if (price) {
        if (!resumed) {
          emitPriceSelected(product, price, initialAmountCents);
        }
        if (initialTrial) {
          void handleStartTrial(product, price);
          return;
        }
        void handleSelectPrice(product, price, initialAmountCents);
        return;
      }
    }
    // The preselected price no longer exists (archived between the config fetch
    // and this mount). Log it — the fall-through to the picker is otherwise
    // indistinguishable from a mount that never had a selection.
    logWarn('preselected price not found; falling back to the plan picker', initialPriceExternalId);
  }, []);

  // beginCheckout starts the Stripe checkout for the given identity and, on
  // success, advances to the payment step and returns 'ok'. On failure it
  // records the error without changing step, leaving the caller to route it:
  // the guest email path stays on the EmailStep (inline error, retry by
  // re-submitting); an 'expired' outcome (the credential was rejected as dead)
  // routes back to the EmailStep to re-identify; any other failure on a
  // logged-in skip goes to the dedicated error step. successStep overrides the
  // advance target: the expedited guest path passes 'email' so the single
  // screen stays mounted and renders the payment form inline off checkoutData.
  // A null credential is the expedited screen's anonymous mount-time start
  // (the deferred-identity path — the backend allows it only on expedited+guest
  // domains). opts.quiet suppresses the inline error + publisher onError for
  // that mount-time start: a failure there degrades silently to the standard
  // non-expedited flow, and the visitor did nothing to report.
  const beginCheckout = async (product: OnsiteProduct, price: OnsitePrice, amountCents: number | null, credential: Credential | null, successStep: CheckoutStep = 'payment', opts?: { quiet?: boolean }): Promise<BeginOutcome> => {
    if (!flowLive()) {
      return 'abandoned';
    }
    setLoading(true);
    setError(null);
    startRequests.advance();
    const startCurrent = startRequests.capture();

    try {
      const result = await startCheckout({
        productExternalId: product.externalId,
        priceExternalId: price.externalId,
        ...(credential ?? {}),
        idempotencyKey: generateIdempotencyKey(),
        surface,
        flow: expeditedActive ? 'expedited' : 'classic',
        requestedAmountCents: product.type === 'donation'
          ? amountCents ?? price.amountCents
          : undefined,
        // Donation amounts are denominated in the currency the paywall
        // presented (a matched currency option, else the price default) —
        // send it so the server charges the same currency the visitor saw.
        ...(product.type === 'donation' ? { currency: price.currency } : {}),
        // Item-scoped prices scope the grant to a single item; product-scoped
        // prices resolve to undefined and send no item.
        item: resolveItemForPrice(price, item),
      });

      if (!flowLive() || !startCurrent()) {
        return 'abandoned';
      }
      setCheckoutData(result);
      // selectionContext, not funnelContext: on the authed-skip path the caller
      // (handleSelectPrice) set the selected state in this same tick, so the
      // funnelContext closure still holds nulls. Gate-path callers pass the
      // same values the settled state already holds, so this is identical there.
      emitFunnelEvent('payment_step_shown', selectionContext(product, price, amountCents));
      setStep(successStep);
      return 'ok';
    } catch (err) {
      if (!flowLive() || !startCurrent()) {
        return 'abandoned';
      }
      logError('checkout start failed', err);
      if (opts?.quiet) {
        return 'failed';
      }
      // A local origin whose hostname resolves no tenant fails the start POST
      // with domain-not-found; route to the local-dev guidance, not the
      // generic error step (same gate as the sdk config fetchers). Report like
      // mountLocalDev does so the failure stays observable to the publisher.
      if (isDomainNotFound(err) && isLocalHostname(window.location.hostname)) {
        reportError({ message: 'local origin cannot resolve a site' });
        setStep('localdev');
        return 'localdev';
      }
      const messageText = err instanceof Error ? err.message : t('checkout.errorGeneric');
      setError(messageText);
      reportError({ message: messageText });
      return isSessionExpired(err) ? 'expired' : 'failed';
    } finally {
      if (flowLive() && startCurrent()) {
        setLoading(false);
      }
    }
  };

  // beginTrial opens the SetupIntent-first trial. On success it advances to the
  // trial payment step (successStep overrides the advance target: the expedited
  // single screen passes 'email' so it stays mounted and renders the trial
  // card-setup form inline off trialData). Gate 1 never denies at start — the
  // paywall is optimistic, so a repeat trialist enters their card like anyone
  // else and confirmTrial's soft denial routes to the trial-ineligible
  // acknowledgment with a saved card to convert on. Other failures route like
  // beginCheckout ('expired' → re-identify on the EmailStep, 'failed' → inline
  // on the email step for guests, error step on a logged-in skip). A null
  // credential is the expedited screen's anonymous mount-time start (the
  // Phase 1b deferred trial start); opts.quiet matches beginCheckout's
  // mount-start degradation.
  // A pending 3DS-return resume (trialSetupResume) is consumed by the first
  // call with a credential: it confirms the trial on that SetupIntent and only
  // falls through to a fresh start when that confirm request fails.
  const beginTrial = async (product: OnsiteProduct, price: OnsitePrice, credential: Credential | null, successStep: CheckoutStep = 'trialPayment', opts?: { quiet?: boolean }): Promise<BeginOutcome> => {
    if (!flowLive()) {
      return 'abandoned';
    }
    setLoading(true);
    setError(null);
    startRequests.advance();
    const startCurrent = startRequests.capture();

    const resume = resumeTrialRef.current;
    const resuming = !!resume && !!credential && price.externalId === initialPriceExternalId;
    try {
      if (resume && credential && resuming) {
        setResumingTrial(true);
        onTrialSetupResumeConsumed?.();
        const outcome = await confirmResumedTrial(product, price, credential, resume, startCurrent);
        if (outcome) {
          return outcome;
        }
      }
      const data = await startTrial({
        productExternalId: product.externalId,
        priceExternalId: price.externalId,
        ...(credential ?? {}),
        idempotencyKey: generateIdempotencyKey(),
      });

      if (!flowLive() || !startCurrent()) {
        return 'abandoned';
      }

      trialCredentialRef.current = credential;
      setTrialData(data);
      // selectionContext over funnelContext for the same same-tick staleness
      // reason as beginCheckout (handleStartTrial's authed skip).
      emitFunnelEvent('payment_step_shown', selectionContext(product, price));
      setStep(successStep);
      return 'ok';
    } catch (err) {
      if (!flowLive() || !startCurrent()) {
        return 'abandoned';
      }
      logError('trial start failed', err);
      if (opts?.quiet) {
        return 'failed';
      }
      // Same local-dev routing as beginCheckout: a local origin with no
      // resolvable tenant fails the trial start with domain-not-found.
      if (isDomainNotFound(err) && isLocalHostname(window.location.hostname)) {
        reportError({ message: 'local origin cannot resolve a site' });
        setStep('localdev');
        return 'localdev';
      }
      const messageText = err instanceof Error ? err.message : t('checkout.errorGeneric');
      setError(messageText);
      reportError({ message: messageText });
      return isSessionExpired(err) ? 'expired' : 'failed';
    } finally {
      if (flowLive() && startCurrent()) {
        setLoading(false);
      }
      if (resuming && flowLive()) {
        setResumingTrial(false);
      }
    }
  };

  // resumeTrialWithProof confirms the 3DS-return resume for a buyer with no
  // credential, proving the SetupIntent with the return's client secret so
  // they see only the starting view. False when there is no proof for this
  // price, or the server refused it (a mismatch, no buyer on the SetupIntent,
  // or a backend without the proof): the caller asks for identity and the
  // resume stays for that credential's confirm.
  const resumeTrialWithProof = async (product: OnsiteProduct, price: OnsitePrice): Promise<boolean> => {
    const resume = resumeTrialRef.current;
    if (!resume?.setupIntentClientSecret || price.externalId !== initialPriceExternalId || !flowLive()) {
      return false;
    }
    setLoading(true);
    setError(null);
    startRequests.advance();
    const startCurrent = startRequests.capture();
    setResumingTrial(true);
    onTrialSetupResumeConsumed?.();
    try {
      const outcome = await confirmResumedTrial(product, price, null, resume, startCurrent);
      // 'expired' re-identifies, which is where the caller goes next anyway.
      return outcome !== null && outcome !== 'expired';
    } finally {
      if (flowLive() && startCurrent()) {
        setLoading(false);
      }
      if (flowLive()) {
        setResumingTrial(false);
      }
    }
  };

  // confirmResumedTrial creates the trial on a SetupIntent whose card check
  // finished on Stripe's 3DS redirect, settling the result like the trial
  // form's confirm (settleTrialCompletion). Any result may already have
  // created a subscription, so it is never restarted. A null credential
  // confirms with the resume's client secret. Returns null only when the
  // server refused the confirm (a 4xx): with a credential the caller starts a
  // fresh card check; with the proof the resume (minus its secret) waits for
  // a credential. A transport/5xx failure keeps the resume so Retry replays
  // the same pinned idempotency key on the same SetupIntent.
  const confirmResumedTrial = async (product: OnsiteProduct, price: OnsitePrice, credential: Credential | null, resume: ReturnedTrialSetup, startCurrent: () => boolean): Promise<BeginOutcome | null> => {
    const secret = resume.setupIntentClientSecret ?? '';
    let completion: CheckoutCompletionResponse;
    try {
      completion = await confirmTrial({
        productExternalId: product.externalId,
        priceExternalId: price.externalId,
        ...(credential ?? { setupIntentClientSecret: secret }),
        idempotencyKey: resume.idempotencyKey,
        setupIntentId: resume.setupIntentId,
        surface,
        flow: resume.flow,
        ...(resume.promoCode ? { promoCode: resume.promoCode } : {}),
      });
    } catch (err) {
      if (!flowLive() || !startCurrent()) {
        return 'abandoned';
      }
      if (isRequestRejected(err) && !credential) {
        resumeTrialRef.current = { ...resume, setupIntentClientSecret: undefined };
        logError('resumed trial proof refused; asking for identity', err);
        return null;
      }
      if (isRequestRejected(err)) {
        resumeTrialRef.current = null;
        logError('resumed trial confirm refused; starting a fresh card check', err);
        return null;
      }
      logError('resumed trial confirm failed; keeping it for retry', err);
      const messageText = err instanceof Error ? err.message : t('checkout.errorGeneric');
      setError(messageText);
      reportError({ message: messageText });
      if (isSessionExpired(err)) {
        return 'expired';
      }
      setStep('error');
      return 'failed';
    }
    resumeTrialRef.current = null;
    if (!flowLive() || !startCurrent()) {
      return 'abandoned';
    }
    trialCredentialRef.current = credential;
    trialProofRef.current = credential ? null : { setupIntentId: resume.setupIntentId, secret };
    // A proven buyer is a guest whose email the page never held, like a
    // deferred start: the account offer reads it off the completion.
    if (!credential) {
      deferredIdentityRef.current = true;
    }
    resumedTrialStripeRef.current = resume.publishableKey ? { publishableKey: resume.publishableKey, connectedAccount: resume.connectedAccount } : null;
    const outcome = await settleTrialCompletion(completion, loadTrialStripe);
    if (!flowLive() || !startCurrent()) {
      return 'abandoned';
    }
    const promoCode = resume.promoCode ?? null;
    const discount = resume.discount ?? null;
    if (outcome.kind === 'ineligible') {
      handleTrialIneligible(resume.setupIntentId, promoCode, discount);
      return 'ok';
    }
    // No card form is mounted to show it beside, so the error step carries it.
    if (outcome.kind === 'failed') {
      setError(outcome.message);
      reportError({ message: outcome.message });
      setStep('error');
      return 'ok';
    }
    handleTrialSuccess(outcome.completion, { promoCode, discount });
    return 'ok';
  };

  // Guest path: a failed start stays on the EmailStep, which renders the error
  // inline so the visitor can correct their email and retry. A trial selection
  // routes through beginTrial.
  // Only the standard email-first flow reaches here — the expedited single
  // screen starts anonymously at mount and collects email via Stripe's elements.
  const handleEmailSubmit = async (email: string) => {
    if (!selectedProduct || !selectedPrice) return;
    // Remember the guest email so the success screen can offer account creation.
    adoptGuestEmail(email);
    setPaymentBackTarget('guest');
    if (trialMode) {
      await beginTrial(selectedProduct, selectedPrice, { email });
      return;
    }
    await beginCheckout(selectedProduct, selectedPrice, selectedAmountCents, { email });
  };

  // An in-page login/register at the gate resolved a credential (BYO email or
  // ezAuth token). A returning member may already hold this entitlement, so
  // establish their session and short-circuit to access instead of charging
  // again; otherwise proceed to checkout (or, for a bare "Already subscribed?
  // Log in" with no price chosen yet, return to the paywall to pick a plan).
  const renderedIdentityCurrent = identityRequests.capture();
  const handleAuthenticated = async (credential: Credential) => {
    if (!flowLive() || switchingIdentityRef.current || !renderedIdentityCurrent()) {
      return;
    }
    identityRequests.advance();
    const identityCurrent = identityRequests.capture();
    const operation = beginSessionRequest();
    const current = () => flowLive() && identityCurrent() && operation.isSignOutCurrent();
    awaitingExternalIdentity.current = false;
    setPaymentBackTarget('authenticated');
    // A gate login supersedes any earlier guest attempt: this checkout is not
    // a guest checkout (no conversion offer on success), and the abandoned
    // guest checkout's data must not leak onto this attempt's screens — the
    // already-entitled short-circuit renders the success step without starting
    // a new checkout.
    adoptGuestEmail(null);
    setConversion(null);
    setCheckoutData(null);
    setCompletionPortalUrl(null);
    setTrialData(null);
    // A gate login supersedes the deferred anonymous attempt too: this
    // checkout is identified, so the success screen must not adopt the
    // completion email into a guest account offer.
    deferredIdentityRef.current = false;
    sessionGuestRef.current = null;
    // A new attempt clears any stale error (e.g. a previous reIdentify
    // message) — the gate's completing view keys its error fallback off it.
    setError(null);
    try {
      await completeGateSignIn(credential, current);
    } finally {
      if (flowLive() && identityCurrent() && !operation.isSignOutCurrent()) {
        setLoading(false);
        setStep('idle');
      }
    }
  };

  const completeGateSignIn = async (credential: Credential, current: () => boolean): Promise<void> => {
    let entitled: boolean;
    try {
      entitled = await isAlreadyEntitled(credential, current);
    } catch (err) {
      if (!current()) {
        return;
      }
      logError('account access lookup failed', err);
      const message = t('inline.accessFailed');
      setError(message);
      reportError({ message });
      setStep('error');
      return;
    }
    if (!current()) {
      return;
    }
    const handle = selectedProduct?.productKey ?? productKey;
    if (hasSession() && handle && selectedProduct?.type !== 'donation') {
      try {
        const options = await getUpgradeOptions(handle, selectedPrice?.externalId);
        if (!current()) {
          return;
        }
        setUpgradeMaskedEmail(options.maskedEmail);
        const source = options.sources[0];
        if (source && (source.inProgress || (!source.blockedReason && source.targets?.length > 0) || source.blockedReason)) {
          if (entitled) {
            clearOwnPendingIntent();
            emitAccessChange();
          }
          setUpgrade(source);
          setUpgradeHasAccess(entitled);
          setStep('idle');
          return;
        }
      } catch (err) {
        if (!current()) {
          return;
        }
        if (switchedFromUpgrade.current) {
          logError('upgrade eligibility lookup failed after switching account', err);
          const message = t('inline.accessFailed');
          setError(message);
          reportError({ message });
          setStep('error');
          return;
        }
        logError('upgrade options unavailable after sign-in; using existing checkout behavior', err);
      }
    }
    if (entitled) {
      clearOwnPendingIntent();
      setAlreadyEntitled(true);
      setSucceeded(true);
      emitAccessChange();
      setStep('success');
      return;
    }
    if (selectedProduct && selectedPrice) {
      // Authenticated at the gate (no email form behind it): an expired
      // credential routes back to the EmailStep to re-identify, any other
      // failed start surfaces on the error step rather than closing silently.
      const useTrial = switchedFromUpgrade.current ? (selectedPrice.trialPeriodDays ?? 0) > 0 : trialMode;
      setTrialMode(useTrial);
      const outcome = useTrial
        ? await beginTrial(selectedProduct, selectedPrice, credential)
        : await beginCheckout(selectedProduct, selectedPrice, selectedAmountCents, credential);
      if (outcome === 'expired') {
        reIdentify();
        return;
      }
      if (outcome === 'failed') {
        setStep('error');
      }
      return;
    }
    setStep('idle');
  };

  // isAlreadyEntitled establishes a content session from the just-resolved
  // credential and checks whether the visitor already holds what the selected
  // price grants — the item for an item-scoped price, otherwise the product. A
  // flaky establish/access call degrades to "not entitled" so a genuine purchase
  // is never blocked. Donations have no entitlement to check.
  const isAlreadyEntitled = async (credential: Credential, current: () => boolean): Promise<boolean> => {
    if (selectedProduct?.type === 'donation') {
      return false;
    }
    const itemScoped = !!selectedPrice && isItemScoped(selectedPrice);
    const purchasedItem = itemScoped && selectedPrice ? resolveItemForPrice(selectedPrice, item) : undefined;
    const handle = selectedProduct?.productKey ?? productKey;
    // Nothing to check against (item-scoped with no resolvable item, or a
    // product check with no handle): treat as not entitled and let checkout run.
    if (itemScoped ? !purchasedItem : !handle) {
      return false;
    }
    try {
      let established = hasSession();
      if (!('session' in credential)) {
        established = switchedFromUpgrade.current ? await establishSession(credential, current) : await establishSession(credential);
      }
      if (!current() || !established) {
        return false;
      }
      const access = itemScoped
        ? await checkItemAccess(purchasedItem!)
        : await checkProductAccess(handle!);
      return access.decision === 'allowed';
    } catch (err) {
      if (switchedFromUpgrade.current) {
        throw err;
      }
      logError('already-entitled check failed', err);
      return false;
    }
  };

  useEffect(() => onAccessChange(() => {
    if (!inline || authMode !== 'byo' || !awaitingExternalIdentity.current || switchingIdentityRef.current || (step !== 'email' && step !== 'login')) {
      return;
    }
    if (hasSession()) {
      void handleAuthenticated({ session: true });
      return;
    }
    // First-time publisher accounts have no SM visitor/session yet.
    const identityCurrent = identityRequests.capture();
    void getIdentityAdapter(authMode, domainId).resolveCredential().then((credential) => {
      if (credential && flowLive() && identityCurrent() && awaitingExternalIdentity.current) {
        void handleAuthenticated(credential);
      }
    });
  }), [inline, authMode, step, switchingIdentity, identityVersion]);

  // fireSuccessHook reports the completed purchase/trial to the publisher's
  // onSuccess. The amount is the recurring/catalog price (a trial charges $0
  // today, but the result describes what was subscribed to). The result shape
  // differs by surface to match the documented contract: a paywall/donation
  // checkout reports the chosen product/price external ids; a direct
  // openCheckout reports the publisher's PriceKey (`price`), and omits the
  // external ids and amount (a per-item purchase's amount is set server-side
  // and not known to the widget).
  const fireSuccessHook = () => {
    const boughtProduct = upgradeTargetRef.current?.product ?? selectedProduct;
    const boughtPrice = upgradeTargetRef.current?.price ?? selectedPrice;
    if (!boughtProduct || !boughtPrice) return;
    // Set for an item-scoped purchase so the publisher learns which item was
    // bought; undefined (omitted) for a whole-product purchase.
    const purchasedItem = resolveItemForPrice(boughtPrice, item);
    const result: CheckoutResult = surface === 'checkout'
      ? { price: boughtPrice.priceKey, item: purchasedItem }
      : {
          productExternalId: boughtProduct.externalId,
          priceExternalId: boughtPrice.externalId,
          productType: boughtProduct.type,
          amountCents: upgradeChargedRef.current ?? donationAmountCents ?? boughtPrice.amountCents,
          product: productKey,
          item: purchasedItem,
        };
    runHook(onSuccess, 'onSuccess', result);
  };

  // finishSuccess is the shared post-finalize seam for paid checkout, trial
  // start, and convert-to-paid: clear resume state, fire the publisher hook,
  // tell on-page gated UI access may have changed, and show the success screen.
  // asTrial selects the success copy (a started trial vs a completed purchase).
  const finishSuccess = (asTrial: boolean) => {
    clearOwnPendingIntent();
    // A completed checkout consumes the campaign stash: without this a later
    // paywall view on another product would prefill (and possibly error on)
    // a code from the campaign the visitor already converted through.
    if (campaignPromo?.source === 'url') {
      clearStashedCampaignPromo();
    }
    setSucceeded(true);
    setSucceededAsTrial(asTrial);
    fireSuccessHook();
    emitAccessChange();
    setStep('success');
  };

  // The server only sends a completion portalUrl when this checkout created
  // the visitor, so a non-empty value always supersedes any start-time link.
  const adoptCompletionPortalUrl = (completion: CheckoutCompletionResponse) => {
    if (completion.portalUrl) {
      setCompletionPortalUrl(completion.portalUrl);
    }
  };

  const handlePaymentSuccess = (completion: CheckoutCompletionResponse, details?: CheckoutSuccessDetails) => {
    adoptCompletionConversion(completion);
    adoptCompletionPortalUrl(completion);
    setSuccessDetails(details ?? null);
    finishSuccess(false);
  };

  // On the deferred-identity paths the widget never held the buyer's email, so
  // the success screen's guest → account offer reads the mask + attestation off
  // the completion. The offer is gated on the widget's own guest markers: a
  // typed gate email (classic guest), a deferred anonymous start (expedited /
  // wallet), or a returning content session with no live site login at
  // selection (possibly an account-less returning guest). A logged-in buyer's
  // completion also carries the fields, and adopting them would wrongly offer
  // account creation.
  const adoptCompletionConversion = (completion: CheckoutCompletionResponse) => {
    const { emailAttestation, maskedEmail } = completion;
    if (!emailAttestation || !maskedEmail) {
      return;
    }
    if (deferredIdentityRef.current || guestEmailRef.current) {
      setConversion({ maskedEmail, emailAttestation });
      return;
    }
    const sessionGuest = sessionGuestRef.current;
    if (!sessionGuest) {
      return;
    }
    // Normally settled long before payment completes; a reset or newer
    // selection while it is pending replaces the ref and drops this result.
    sessionGuest.then((guest) => {
      if (guest && sessionGuestRef.current === sessionGuest && flowLive()) {
        setConversion({ maskedEmail, emailAttestation });
      }
    }).catch((err) => logError('conversion offer identity check failed', err));
  };

  // Creates the trialing subscription on the SetupIntent's confirmed card. Passed
  // to the trial forms, which have just confirmed the card with Stripe and hand
  // back the SetupIntent id; the credential + identifiers come from flow state.
  // The deferred path has no start-time credential: the form's email (typed or
  // wallet-supplied) becomes the credential, and is adopted into trialCredential
  // so a later convert-to-paid replays it.
  const handleConfirmTrial = (setupIntentId: string, promoCode: string | null, email?: string) => {
    if (!selectedProduct || !selectedPrice) {
      return Promise.reject(new Error(t('checkout.sessionInvalid')));
    }
    let credential = trialCredentialRef.current;
    if (!credential) {
      if (!email) {
        return Promise.reject(new Error(t('checkout.sessionInvalid')));
      }
      credential = { email };
      trialCredentialRef.current = credential;
    }
    return confirmTrial({
      productExternalId: selectedProduct.externalId,
      priceExternalId: selectedPrice.externalId,
      ...credential,
      idempotencyKey: generateIdempotencyKey(),
      setupIntentId,
      surface,
      flow: expeditedActive ? 'expedited' : 'classic',
      ...(promoCode ? { promoCode } : {}),
    });
  };

  const handleTrialSuccess = (completion: CheckoutCompletionResponse, details?: CheckoutSuccessDetails) => {
    adoptCompletionConversion(completion);
    adoptCompletionPortalUrl(completion);
    setSuccessDetails(details ?? null);
    finishSuccess(true);
  };

  // Trial-gate soft denial at confirm time: Gate 2 (the card already trialed
  // this product) or Gate 1 (this visitor already trialed — Gate 1 runs only
  // at confirm, so the card is always saved by denial time). Keep the
  // confirmed SetupIntent id and route to the trial-ineligible
  // acknowledgment, whose Subscribe converts to paid on that card. The convert
  // idempotency key is pinned here so a retried Subscribe click replays the same
  // Stripe create instead of minting a second incomplete subscription. The promo
  // code comes from the denying form's own state, pinned with it — the ref feeds
  // the convert API call, the state feeds the acknowledgment's display.
  const handleTrialIneligible = (setupIntentId: string, promoCode: string | null, discount: TrialDiscount | null) => {
    setPendingSetupIntentId(setupIntentId);
    convertIdempotencyKeyRef.current = generateIdempotencyKey();
    convertPromoCodeRef.current = promoCode;
    setIneligiblePromo(promoCode ? { code: promoCode, discount, firstCharge: null } : null);
    setConvertCreated(false);
    setConvertFailed(false);
    setError(null);
    setStep('trialIneligible');
    // The acknowledgment's exact-charge breakdown is the only reader of the
    // first-charge preview, so it is fetched here — not on the trial step's
    // apply, which would pay a Stripe invoice-preview call on every apply.
    // Until it lands the acknowledgment renders its catalog-price fallback
    // (the same state a preview failure renders).
    if (!promoCode || !selectedProduct || !selectedPrice) {
      return;
    }
    validatePromoCode({
      productExternalId: selectedProduct.externalId,
      priceExternalId: selectedPrice.externalId,
      code: promoCode,
      includeFirstChargePreview: true,
    }).then((result) => {
      // Merge only while the acknowledgment still shows the code this fetch
      // was for — a remove/re-apply on the ack owns the state from then on.
      if (!result.valid) {
        logWarn('trial-ineligible first-charge preview came back invalid', { code: promoCode, reason: result.reason });
        return;
      }
      setIneligiblePromo((prev) => (prev && prev.code === promoCode ? { ...prev, firstCharge: firstChargeFromValidation(result) } : prev));
    }, (err) => {
      logWarn('trial-ineligible first-charge preview failed', err);
    });
  };

  // The ack's promo apply re-pins the code the convert sends, re-validated
  // against the selected product/price via the same endpoint the trial form
  // uses. The convert re-validates server-side regardless — this is advisory,
  // so the chip and sidebar note match what the convert will attempt.
  const handleAckPromoApply = async (code: string): Promise<PromoApplyResult> => {
    if (!selectedProduct || !selectedPrice) {
      return { ok: false, message: t('promo.invalid', { code }) };
    }
    const result = await validatePromoCode({
      productExternalId: selectedProduct.externalId,
      priceExternalId: selectedPrice.externalId,
      code,
      // The acknowledgment shows the preview as the convert's order-summary
      // breakdown.
      includeFirstChargePreview: true,
    });
    if (!result.valid) {
      return { ok: false, message: promoReasonMessage(code, result.reason) };
    }
    const applied = result.code ?? code;
    convertPromoCodeRef.current = applied;
    promoCodeRef.current = applied;
    setIneligiblePromo({ code: applied, discount: promoDiscountFromValidation(result), firstCharge: firstChargeFromValidation(result) });
    return { ok: true };
  };

  // The ack's promo remove is purely local: the code is only attached at
  // convert, so there is nothing server-side to undo.
  const handleAckPromoRemove = async (): Promise<PromoApplyResult> => {
    convertPromoCodeRef.current = null;
    promoCodeRef.current = null;
    setIneligiblePromo(null);
    return { ok: true };
  };

  // The convert success screen's display context: the ack's carried (or
  // re-pinned) promo, plus the first invoice's actual totals when the convert
  // completion carries them — the convert has no Checkout Session, so without
  // these the sidebar would show the catalog price for a discounted charge.
  const convertSuccessDetails = (completion: CheckoutCompletionResponse): CheckoutSuccessDetails => ({
    promoCode: ineligiblePromo?.code ?? null,
    discount: ineligiblePromo?.discount ?? null,
    charged: completion.chargedAmountCents != null && completion.chargedCurrency
      ? { amountCents: completion.chargedAmountCents, currency: completion.chargedCurrency, discountCents: completion.chargedDiscountCents }
      : undefined,
  });

  // Convert-to-paid: start a normal (non-trial) subscription on the card already
  // saved by the SetupIntent. The acknowledgment step the confirm-time trial
  // denials route to; succeeds into the paid success copy. When the first invoice needs
  // on-session authentication (3DS), or a retry after a decline or failed 3DS,
  // the pending response carries its PaymentIntent's client secret and the
  // saved card: confirm it (modal-first; a full-page redirect resumes via the
  // pending-checkout marker), then poll the completion endpoint for the grant.
  // A retry pays the same subscription's first invoice: the pinned key replays
  // the same create.
  const showConvertFailure = (reason?: SubscriptionPaymentFailureReason) => {
    const msg = reason ? t(`trialIneligible.reason.${reason}`) : t('paymentForm.failed');
    setError(msg);
    setConvertFailed(true);
    reportError({ message: msg, ...(reason ? { code: reason } : {}) });
  };

  // The convert identifies the buyer like the confirm did: the flow's
  // credential, else the 3DS-return proof for this same SetupIntent.
  const convertIdentity = (setupIntentId: string): Credential | { setupIntentClientSecret: string } | null => {
    if (trialCredentialRef.current) {
      return trialCredentialRef.current;
    }
    const proof = trialProofRef.current;
    if (!proof || proof.setupIntentId !== setupIntentId) {
      return null;
    }
    return { setupIntentClientSecret: proof.secret };
  };

  const handleConvertToPaid = async () => {
    const identity = pendingSetupIntentId ? convertIdentity(pendingSetupIntentId) : null;
    if (!selectedProduct || !selectedPrice || !identity || !pendingSetupIntentId || !convertIdempotencyKeyRef.current) {
      setStep('error');
      return;
    }
    const convert = () => convertToPaid({
      productExternalId: selectedProduct.externalId,
      priceExternalId: selectedPrice.externalId,
      ...identity,
      idempotencyKey: convertIdempotencyKeyRef.current ?? '',
      setupIntentId: pendingSetupIntentId,
      surface,
      flow: expeditedActive ? 'expedited' : 'classic',
      // Pinned at the Gate-2 handoff from the denying form's own state, so
      // the convert re-offers the discount the visitor applied on the trial
      // step — never a stale code from an earlier selection.
      ...(convertPromoCodeRef.current ? { promoCode: convertPromoCodeRef.current } : {}),
    });
    setLoading(true);
    setError(null);
    try {
      let completion = await convert();
      let created = !convertCreated;
      // Stripe voids an unpaid first invoice ~23h after create; the pinned key
      // would replay that dead subscription forever, so start one new convert.
      if (completion.paymentState === 'expired') {
        convertIdempotencyKeyRef.current = generateIdempotencyKey();
        completion = await convert();
        created = true;
      }
      setConvertCreated(true);
      // A create already attempted the first charge; confirming now would
      // decline the same card a second time. Show the failure; the buyer's
      // next Subscribe is the retry.
      if (completion.paymentState === 'failed' && created) {
        showConvertFailure(completion.failureReason);
        return;
      }
      if (completion.status === 'completed') {
        adoptCompletionConversion(completion);
        adoptCompletionPortalUrl(completion);
        setSuccessDetails(convertSuccessDetails(completion));
        finishSuccess(false);
        return;
      }
      // The ack's convert is only reachable from a confirm-time denial — the
      // trial form's (trialData) or a 3DS-return resume's (parked keys) — so
      // one of them is the Stripe init for the confirm.
      const stripeKeys = trialStripeKeys();
      if (completion.clientSecret && stripeKeys) {
        const stripe = await getStripe(stripeKeys.publishableKey, stripeKeys.connectedAccount);
        const confirmed = await confirmSubscriptionPayment(stripe, completion.checkoutId, completion.clientSecret, completion.paymentMethodId);
        if (confirmed.status === 'completed') {
          adoptCompletionConversion(confirmed.completion);
          adoptCompletionPortalUrl(confirmed.completion);
          setSuccessDetails(convertSuccessDetails(confirmed.completion));
          finishSuccess(false);
          return;
        }
        if (confirmed.status === 'failed') {
          showConvertFailure(confirmed.reason);
          return;
        }
      }
      const msg = t('checkout.convertProcessing');
      setError(msg);
      reportError({ message: msg });
    } catch (err) {
      logError('convert-to-paid failed', err);
      const messageText = err instanceof Error ? err.message : t('checkout.errorGeneric');
      setError(messageText);
      reportError({ message: messageText });
    } finally {
      setLoading(false);
    }
  };

  // Back and "Add new payment method" on the ack reopen the card form on a
  // new SetupIntent: the denied one already succeeded and Stripe will not
  // confirm it again. A new card that is denied again comes back here with a
  // new convert key, so its convert creates a new subscription and the server
  // cancels the abandoned unpaid one.
  // On the expedited single screen that screen is the payment screen, restarted
  // anonymously the same way its mount-time start ran.
  const handleIneligibleToPayment = () => {
    if (!selectedProduct || !selectedPrice) {
      setStep('error');
      return;
    }
    setError(null);
    if (expeditedActive) {
      void runExpeditedStart(() => beginTrial(selectedProduct, selectedPrice, null, 'email', { quiet: true })).then((started) => {
        if (!started) {
          setStep('email');
        }
      });
      return;
    }
    // A buyer identified only by the 3DS-return proof identifies for the new
    // card check like any anonymous one.
    if (!trialCredentialRef.current) {
      setStep('email');
      return;
    }
    void beginTrial(selectedProduct, selectedPrice, trialCredentialRef.current);
  };

  const closeIdentity = () => {
    flowClosed.current = true;
    invalidateIdentity();
  };

  // Dismissing before a successful checkout is a publisher-visible cancel;
  // dismissing the success screen is just closing the chrome.
  const handleDismiss = onDismiss
    ? () => {
        flowClosed.current = true;
        invalidateIdentity();
        // Exiting checkout abandons any resume intent.
        clearOwnPendingIntent();
        if (!succeeded) {
          if (!upgrade) {
            emitFunnelEvent('modal_dismissed', funnelContext());
          }
          runHook(onCancel, 'onCancel');
        }
        onDismiss();
      }
    : undefined;

  const handleLoginSubmit = async (email: string) => {
    setLoading(true);
    setError(null);

    try {
      await requestMagicLink({
        email,
        domainId,
        returnUrl: window.location.href,
      });
      setLoginLinkSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('checkout.magicLinkFailed'));
    } finally {
      setLoading(false);
    }
  };

  // BYO auth may open the publisher's own modal in-page (OV) instead of
  // navigating away — the gate must unmount or it covers the publisher's
  // modal. Not handleDismiss: the parked resume intent must survive (resume
  // fires on authChanged) and an external login is not a cancel. Prefer the
  // always-wired exit prop; onDismiss is the fallback for mount sites without
  // one.
  const dismissForExternalAuth = () => {
    if (inline) {
      return;
    }
    flowClosed.current = true;
    invalidateIdentity();
    (onExternalAuthExit ?? onDismiss)?.();
  };

  // Persist a resume intent before a BYO navigate-away (login or account
  // creation) at the gate, with the chosen price so resume lands on payment.
  // ezAuth (in-page) never calls this. No product handle means nothing to resume.
  // A direct-checkout (openCheckout) mount parks kind 'item' with the price KEY
  // so the resume re-runs openCheckout — re-resolving the single-price config
  // and keeping surface 'checkout' — rather than mounting the full-product
  // paywall the product-kind branch would build.
  const persistGateAuthIntent = () => {
    if (inline) {
      return;
    }
    const selection = resumableSelection();
    if (!selection) {
      return;
    }
    setPendingIntent(selection);
  };

  // The selection a resume re-opens (see persistGateAuthIntent), or null when
  // this mount has nothing resumable.
  const resumableSelection = (): Omit<PendingIntent, 'originPath' | 'ts'> | null => {
    if (surface === 'checkout') {
      const priceKey = selectedPrice?.priceKey;
      if (!priceKey) {
        return null;
      }
      return { kind: 'checkout', price: priceKey, item, dismissible: !!onDismiss, promo: activeCampaignPromo ?? undefined, anchor: dialogAnchorSelector() };
    }
    const handle = productKey ?? selectedProduct?.productKey;
    if (!handle) {
      return null;
    }
    return { kind: 'product', product: handle, priceExternalId: selectedPrice?.externalId, item, dismissible: !!onDismiss, promo: activeCampaignPromo ?? undefined, anchor: dialogAnchorSelector() };
  };

  // Parks the trial selection while the card check is in flight: an issuer's
  // 3DS redirect unloads the page, and the sdk's return handler resumes from
  // this park. A failed park must not block the payment, so it only warns.
  // Inline mounts don't park: the resume can only reopen a modal.
  const handleSetupConfirmStart = (setupIntentId: string, promoCode: string | null, discount: TrialDiscount | null) => {
    if (inline) {
      return;
    }
    const selection = resumableSelection();
    if (!selection) {
      logWarn('no resumable selection to park for a trial 3DS redirect', setupIntentId);
      return;
    }
    ownTrialParkRef.current = setupIntentId;
    setPendingIntent({
      ...selection,
      trialSetup: {
        setupIntentId,
        idempotencyKey: generateIdempotencyKey(),
        flow: expeditedActive ? 'expedited' : 'classic',
        promoCode: promoCode ?? undefined,
        discount: discount ?? undefined,
        publishableKey: trialData?.publishableKey,
        connectedAccount: trialData?.providerAccount,
      },
    });
  };

  // clearOwnPendingIntent is the exit paths' park clear (success, dismiss,
  // identity switch): it drops a BYO login park as before, but leaves another
  // flow's trial park (another tab mid-3DS) for its own return.
  const clearOwnPendingIntent = () => {
    const parkedTrial = getPendingIntent()?.trialSetup;
    if (parkedTrial && parkedTrial.setupIntentId !== ownTrialParkRef.current) {
      return;
    }
    clearPendingIntent();
  };

  // The card check returned on this page, so the in-page flow owns the outcome.
  // Clears only this check's park, never a BYO login park.
  const handleSetupConfirmSettled = (setupIntentId: string) => {
    if (getPendingIntent()?.trialSetup?.setupIntentId !== setupIntentId) {
      return;
    }
    clearPendingIntent();
  };

  // "Already subscribed? Log in" on the paywall routes through the mode adapter:
  // ezAuth opens the passwordless-first login screen (the 'login' step —
  // magic-link email primary, password form opt-in), BYO navigates away to the
  // publisher's login.
  const handlePaywallLogin = () => {
    setError(null);
    const adapter = getIdentityAdapter(authMode, domainId);
    if (adapter.capabilities.loginInPage) {
      setStep('login');
      return;
    }
    // BYO navigate-away from the paywall (no plan chosen yet): resume re-opens
    // the paywall so the visitor can pick a plan as a logged-in user.
    const handle = productKey ?? products[0]?.productKey;
    if (handle && !inline) {
      setPendingIntent({ kind: 'product', product: handle, item, dismissible: !!onDismiss, promo: activeCampaignPromo ?? undefined, anchor: dialogAnchorSelector() });
    }
    adapter.promptLogin();
    dismissForExternalAuth();
  };

  // Login from the expedited single screen: same routing as the paywall login,
  // but the visitor has already picked a price, so a BYO navigate-away parks
  // the gate-intent shape (with priceExternalId) and resume lands on payment.
  const handleExpeditedLogin = () => {
    setError(null);
    const adapter = getIdentityAdapter(authMode, domainId);
    if (adapter.capabilities.loginInPage) {
      setStep('login');
      return;
    }
    persistGateAuthIntent();
    adapter.promptLogin();
    dismissForExternalAuth();
  };

  // The magic-link handoff from the login screen. A typed email (the
  // passwordless-first primary submit) fires the send immediately — the button
  // said "email me a sign-in link", so no second confirm click; a send failure
  // lands on the pre-filled form with the error inline. A bare handoff (the
  // checkout-gate password form's fallback link carries no email — ezAuth owns
  // that field) just opens the form.
  const handleMagicLink = (email?: string) => {
    setError(null);
    setLoginLinkSent(false);
    setMagicLinkEmail(email ?? '');
    setStep('magiclink');
    if (email) {
      void handleLoginSubmit(email);
    }
  };

  // Back from payment returns to the step that advanced here: the guest email
  // form (prefilled, via EmailStep's initialView/initialEmail) for a guest, the
  // plan picker for an authenticated visitor — re-showing the "Sign in to
  // continue" gate would ask them to identify again when they just did. A direct
  // openCheckout has no plan picker, so an authenticated visitor there gets no
  // Back (matching the old ItemPurchaseFlow); a guest still returns to the email
  // form.
  const handlePaymentBack = () => {
    setError(null);
    if (paymentBackTarget === 'guest') {
      setStep('email');
      return;
    }
    setStep('idle');
  };
  // Whether a payment/trial step offers Back at all: a guest always can (to the
  // email form); an authenticated visitor only on a surface with a plan picker.
  const showPaymentBack = paymentBackTarget === 'guest' || canGoBackToPicker;

  // Back from the expedited single screen returns to the plan picker and
  // abandons the selection: the anonymous mount-time start has already fired,
  // so the checkout/trial data must not survive to remount against the next
  // pick, and the picker login's Back must not route back here off a stale
  // selectedPrice. The generation bump discards the in-flight start's result
  // (the abandoned Stripe session expires on its own; a new pick mints a new
  // idempotency key).
  const handleExpeditedBack = () => {
    startRequests.advance();
    setError(null);
    setLoading(false);
    setCheckoutData(null);
    setCompletionPortalUrl(null);
    setTrialData(null);
    setTrialMode(false);
    trialCredentialRef.current = null;
    trialProofRef.current = null;
    deferredIdentityRef.current = false;
    sessionGuestRef.current = null;
    setSelectedProduct(null);
    setSelectedPrice(null);
    setSelectedAmountCents(null);
    setPaymentBackTarget(null);
    promoCodeRef.current = null;
    setStep('idle');
  };

  const handleClose = () => {
    if (inline) {
      return;
    }
    flowClosed.current = true;
    invalidateIdentity();
    onUnlock();
  };

  // The campaign code's discount on the selected price (the config promo
  // block), previewed on the identity step's order summary so it matches the
  // picker. Keys off the live campaign state — a dropped (foreign/removed)
  // code shows the plain price.
  const emailPromoDiscount = activeCampaignPromo && selectedPrice ? promoPreview?.discounts?.[selectedPrice.externalId] : undefined;
  // Its first charge, from the server-computed (Stripe-rounded) discounted
  // amount. Donations never carry a promo block, so the catalog amount is the
  // undiscounted base.
  let emailPromoCharged: ChargedTotals | undefined;
  if (emailPromoDiscount && selectedPrice) {
    emailPromoCharged = {
      amountCents: emailPromoDiscount.discountedAmountCents,
      currency: emailPromoDiscount.currency,
      discountCents: selectedPrice.amountCents - emailPromoDiscount.discountedAmountCents,
    };
  }

  // Request cancellation must not unmount the shell before its dismissal timer fires.
  if (isActive && !isActive()) {
    return null;
  }
  if (switchingIdentity) {
    return <CheckoutShell ariaLabel={t('emailStep.title.signIn')} currentStep={1} hideSteps onDismiss={handleDismiss} onDismissRequested={closeIdentity}>
      <p role="status" aria-label={t('emailStep.title.signIn')}><span class="sm-btn-spinner" aria-hidden="true" /></p>
    </CheckoutShell>;
  }
  if (upgrade) {
    // Closable when the visitor already has access or the screen only explains
    // a blocker (nothing to buy), whatever the paywall's dismissible option.
    const closable = upgradeHasAccess || !!upgrade.blockedReason;
    const dismissUpgrade = handleDismiss ?? (closable ? () => {
      if (!succeeded) {
        runHook(onCancel, 'onCancel');
      }
      onExternalAuthExit?.();
    } : undefined);
    return <UpgradeStep
      key={identityVersion}
      source={upgrade}
      authMode={authMode}
      domainId={domainId}
      maskedEmail={upgradeMaskedEmail}
      onSwitchIdentity={onSwitchIdentity ? (product, price) => { void handleSwitchIdentity(product, price); } : undefined}
      refreshSource={async () => {
        const handle = selectedProduct?.productKey ?? productKey;
        if (!handle) {
          return undefined;
        }
        const identityCurrent = identityRequests.capture();
        const options = await getUpgradeOptions(handle, selectedPrice?.externalId ?? initialPriceExternalId);
        if (!flowLive() || !identityCurrent()) {
          return undefined;
        }
        setUpgradeMaskedEmail(options.maskedEmail);
        return options.sources[0];
      }}
      products={products}
      initialPriceExternalId={selectedPrice?.externalId ?? initialPriceExternalId}
      onSelect={(product, price) => {
        if (!flowLive()) {
          return;
        }
        setSelectedProduct(product);
        setSelectedPrice(price);
      }}
      onSuccess={(result, product, price) => {
        if (!flowLive()) {
          return;
        }
        upgradeChargedRef.current = result.amountChargedCents;
        if (product && price) {
          upgradeTargetRef.current = { product, price };
        }
        finishSuccess(false);
      }}
      onClose={handleClose}
      onDismiss={dismissUpgrade}
    />;
  }

  let emailInitialView: 'guest' | 'auth' | undefined;
  if (switchedFromUpgrade.current && authMode !== 'byo') {
    emailInitialView = 'auth';
  }
  if (paymentBackTarget === 'guest') {
    emailInitialView = 'guest';
  }

  // The resumed confirm (it can take seconds) shows the trial's starting
  // view: the step behind it is an email gate already answered, or the
  // expedited screen with no form to show.
  if (resumingTrial) {
    return (
      <CheckoutShell ariaLabel={t('trialPayment.starting')} currentStep={2} hideSteps onDismiss={handleDismiss}>
        <h2 class="sm-title">{t('trialPayment.starting')}</h2>
        <div class="sm-form-skeleton" aria-hidden="true">
          <div class="sm-form-skeleton-field" />
          <div class="sm-form-skeleton-button" />
        </div>
      </CheckoutShell>
    );
  }

  switch (step) {
    case 'idle':
      // Suppress the picker while a preselected mount's auto-select is still
      // running (no picker flash). A direct openCheckout never shows the picker
      // at all — there is no plan to pick — so post-mount 'idle' renders
      // nothing on that surface. The paywall/donation surfaces render the
      // picker normally once the auto-select has run (Back buttons, error
      // retry, a vanished price all land here).
      if (!autoSelectDone || !canGoBackToPicker) {
        return null;
      }
      return (
        <PaywallOverlay
          products={products}
          checkoutText={checkoutText}
          item={item}
          // The picker's campaign discounts key off the live campaign state,
          // not just the config block: a dropped (foreign/removed) code shows
          // full prices.
          promoDiscounts={activeCampaignPromo ? promoPreview?.discounts : undefined}
          promoCode={activeCampaignPromo?.code}
          initialAmountCents={initialAmountCents}
          maskedEmail={maskedIdentity}
          onSwitchIdentity={onSwitchIdentity}
          onSelectPrice={pickPrice}
          onStartTrial={pickTrial}
          onDismiss={handleDismiss}
          onLogin={handlePaywallLogin}
        />
      );

    // Insecure page: the selection is parked in the ezsm_resume URL param and
    // the interstitial reloads over https; bootstrap resumes checkout from it.
    case 'secureRedirect': {
      if (!selectedPrice) return null;
      // A direct-checkout (openCheckout) mount parks kind 'item' with the price
      // KEY so the https landing re-runs openCheckout (single-price config,
      // surface 'checkout'); a paywall/donation mount parks the product.
      const payload: SecureResumePayload = surface === 'checkout'
        ? {
            v: 1,
            kind: 'checkout',
            price: selectedPrice.priceKey,
            item,
            dismissible: !!onDismiss,
            // sessionStorage doesn't cross the http→https origin boundary — the
            // campaign code rides the payload.
            promo: activeCampaignPromo ?? undefined,
          }
        : {
            v: 1,
            kind: 'product',
            product: productKey ?? selectedProduct?.productKey,
            priceExternalId: selectedPrice.externalId,
            amountCents: selectedAmountCents ?? undefined,
            trial: trialMode || undefined,
            item,
            dismissible: !!onDismiss,
            promo: activeCampaignPromo ?? undefined,
          };
      if (payload.kind === 'product' && !payload.product) return null;
      return <SecureRedirectStep payload={payload} onDismiss={handleDismiss} />;
    }

    case 'email':
      if (!selectedProduct || !selectedPrice) return null;
      if (expeditedActive) {
        return (
          <ExpeditedCheckoutStep
            product={selectedProduct}
            price={selectedPrice}
            amountCentsOverride={donationAmountCents}
            domainId={domainId}
            checkoutData={checkoutData}
            trialData={trialData}
            trialPeriodDays={trialMode ? selectedPrice.trialPeriodDays : undefined}
            starting={loading}
            onSuccess={trialMode ? handleTrialSuccess : handlePaymentSuccess}
            onWalletConfirm={() => recordIdentityMethod('wallet')}
            onCardSubmit={() => recordIdentityMethod('guest')}
            onWalletsReady={() => emitFunnelEvent('wallets_ready', funnelContext())}
            walletsEnabled={walletsEnabled}
            onTrialConfirm={handleConfirmTrial}
            onTrialIneligible={handleTrialIneligible}
            onSetupConfirmStart={handleSetupConfirmStart}
            onSetupConfirmSettled={handleSetupConfirmSettled}
            onError={handlePaymentError}
            onPromoChange={handlePromoChange}
            campaignPromo={activeCampaignPromo}
            onCampaignApply={handleCampaignApply}
            onCampaignApplyFailed={handleCampaignApplyFailed}
            onLogin={handleExpeditedLogin}
            onBack={canGoBackToPicker ? handleExpeditedBack : undefined}
            onDismiss={handleDismiss}
          />
        );
      }
      return (
        <EmailStep
          key={identityVersion}
          onDismissRequested={closeIdentity}
          product={selectedProduct}
          price={selectedPrice}
          purchaseName={selectedProduct.paywallText?.title || selectedProduct.name}
          amountCentsOverride={donationAmountCents}
          trialPeriodDays={trialMode ? selectedPrice.trialPeriodDays : undefined}
          discount={emailPromoDiscount ? trialDiscountFromPromo(emailPromoDiscount) : undefined}
          charged={emailPromoCharged}
          authMode={authMode}
          domainId={domainId}
          guestCheckoutEnabled={guestCheckoutEnabled !== false}
          initialView={emailInitialView}
          initialEmail={guestEmail ?? (switchedFromUpgrade.current ? undefined : initialEmail)}
          recordIdentityMethod={recordIdentityMethod}
          onSubmit={handleEmailSubmit}
          onAuthenticated={handleAuthenticated}
          onMagicLink={handleMagicLink}
          onExternalAuth={() => {
            awaitingExternalIdentity.current = switchedFromUpgrade.current;
            persistGateAuthIntent();
            dismissForExternalAuth();
          }}
          onBack={canGoBackToPicker ? () => setStep('idle') : undefined}
          onDismiss={handleDismiss}
          loading={loading}
          error={error}
        />
      );

    case 'payment':
      if (!selectedProduct || !checkoutData || !selectedPrice) return null;
      return (
        <PaymentStep
          product={selectedProduct}
          price={selectedPrice}
          amountCentsOverride={donationAmountCents}
          clientSecret={checkoutData.clientSecret}
          publishableKey={checkoutData.publishableKey}
          checkoutId={checkoutData.checkoutId}
          connectedAccount={checkoutData.providerAccount}
          domainId={domainId}
          maskedEmail={checkoutData.maskedEmail}
          promotionCodesEnabled={checkoutData.promotionCodesEnabled}
          savePaymentMethod={checkoutData.savePaymentMethod}
          showExpressCheckout
          walletsEnabled={walletsEnabled}
          onSwitchIdentity={onSwitchIdentity}
          onWalletsReady={() => emitFunnelEvent('wallets_ready', funnelContext())}
          onPromoChange={handlePromoChange}
          campaignPromo={activeCampaignPromo}
          onCampaignApply={handleCampaignApply}
          onCampaignApplyFailed={handleCampaignApplyFailed}
          onSuccess={handlePaymentSuccess}
          onError={handlePaymentError}
          onBack={showPaymentBack ? handlePaymentBack : undefined}
          onDismiss={handleDismiss}
        />
      );

    case 'trialPayment':
      if (!selectedProduct || !selectedPrice || !trialData) return null;
      return (
        <TrialPaymentStep
          product={selectedProduct}
          price={selectedPrice}
          trialPeriodDays={selectedPrice.trialPeriodDays}
          clientSecret={trialData.clientSecret}
          customerSessionClientSecret={trialData.customerSessionClientSecret}
          publishableKey={trialData.publishableKey}
          connectedAccount={trialData.providerAccount}
          maskedEmail={trialData.maskedEmail}
          email={guestEmail ?? undefined}
          promotionCodesEnabled={trialData.promotionCodesEnabled}
          showExpressCheckout
          walletsEnabled={walletsEnabled}
          onSwitchIdentity={onSwitchIdentity}
          onWalletsReady={() => emitFunnelEvent('wallets_ready', funnelContext())}
          onConfirm={handleConfirmTrial}
          onPromoChange={handlePromoChange}
          campaignPromo={activeCampaignPromo}
          onCampaignApply={handleCampaignApply}
          onSuccess={handleTrialSuccess}
          onTrialIneligible={handleTrialIneligible}
          onSetupConfirmStart={handleSetupConfirmStart}
          onSetupConfirmSettled={handleSetupConfirmSettled}
          onError={handlePaymentError}
          onBack={showPaymentBack ? handlePaymentBack : undefined}
          onDismiss={handleDismiss}
        />
      );

    case 'trialIneligible':
      if (!selectedProduct || !selectedPrice) return null;
      return (
        <TrialIneligibleStep
          product={selectedProduct}
          price={selectedPrice}
          promoCode={ineligiblePromo?.code ?? null}
          discount={ineligiblePromo?.discount ?? null}
          firstCharge={ineligiblePromo?.firstCharge ?? null}
          promotionCodesEnabled={trialData?.promotionCodesEnabled}
          onApplyPromo={handleAckPromoApply}
          onRemovePromo={handleAckPromoRemove}
          promoLocked={convertCreated}
          // The trial's own mask, not maskedIdentity: a stale paid-checkout
          // mask (checkoutData survives Back) must not name the wrong identity
          // on a screen whose convert charges the trial's saved card.
          maskedEmail={trialData?.maskedEmail}
          // Every denial is confirm-time with the card already saved, so
          // Subscribe always converts on it — no card re-entry.
          onSubscribe={handleConvertToPaid}
          onAddPaymentMethod={convertFailed ? handleIneligibleToPayment : undefined}
          // The card form's own Back reaches the plan picker.
          onBack={handleIneligibleToPayment}
          onDismiss={handleDismiss}
          loading={loading}
          error={error}
        />
      );

    case 'success':
      if (switchedFromUpgrade.current && alreadyEntitled) {
        return <CheckoutShell ariaLabel={t('success.title.owned')} currentStep={3} hideSteps onDismiss={handleDismiss}>
          <h2 class="sm-title" role="status">{t('success.title.owned')}</h2>
          {inline && successUrl && <a class="sm-btn sm-btn-primary" href={successUrl}>{t('cta.continue')}</a>}
          {!inline && <button type="button" class="sm-btn sm-btn-primary" onClick={handleClose}>{t('cta.continue')}</button>}
        </CheckoutShell>;
      }
      return (
        <SuccessStep
          alreadyEntitled={alreadyEntitled}
          trial={succeededAsTrial}
          trialPeriodDays={selectedPrice?.trialPeriodDays}
          product={selectedProduct ?? undefined}
          price={selectedPrice ?? undefined}
          amountCentsOverride={donationAmountCents}
          summary={successDetails?.session}
          promoCode={successDetails?.promoCode ?? null}
          discount={successDetails?.discount ?? null}
          charged={successDetails?.charged ?? null}
          maskedEmail={checkoutData?.maskedEmail ?? trialData?.maskedEmail}
          portalUrl={completionPortalUrl ?? checkoutData?.portalUrl ?? trialData?.portalUrl}
          conversion={conversion ?? undefined}
          authMode={authMode}
          domainId={domainId}
          onClose={handleClose}
          onDismiss={handleDismiss}
        />
      );

    // Adapter login (ezAuth's in-page form) from "Already subscribed? Log in".
    // No recordIdentityMethod: this is a returning-subscriber unlock, not a
    // purchase, so it never records an identity_method_selected.
    case 'login':
      return (
        <EmailStep
          key={identityVersion}
          onDismissRequested={closeIdentity}
          intent="login"
          authMode={authMode}
          domainId={domainId}
          guestCheckoutEnabled={false}
          onExternalAuth={() => {
            awaitingExternalIdentity.current = switchedFromUpgrade.current;
            persistGateAuthIntent();
            dismissForExternalAuth();
          }}
          onSubmit={handleEmailSubmit}
          onAuthenticated={handleAuthenticated}
          onMagicLink={handleMagicLink}
          // From the expedited screen a price is already chosen, so Back
          // returns to it; from the bare paywall there is nothing to return
          // to but the plan picker. A direct openCheckout has no picker, so no
          // Back.
          onBack={canGoBackToPicker ? () => setStep(expedited && selectedPrice ? 'email' : 'idle') : undefined}
          onDismiss={handleDismiss}
          loading={loading}
          error={error}
        />
      );

    // Magic-link step ("Email me a sign-in link").
    case 'magiclink':
      return (
        <LoginStep
          initialEmail={magicLinkEmail || undefined}
          onSubmit={handleLoginSubmit}
          // Back returns to the email/gate step on a direct checkout (no picker
          // there); on the paywall it returns to the plan picker.
          onBack={() => setStep(canGoBackToPicker ? 'idle' : 'email')}
          onDifferentEmail={() => {
            setError(null);
            setLoginLinkSent(false);
          }}
          loading={loading}
          sent={loginLinkSent}
          error={error}
          onDismiss={handleDismiss}
        />
      );

    // A checkout-start failure with no email form behind it. "Try again" returns
    // to the plan picker so the visitor can re-select and retry — except on a
    // direct openCheckout, which has no picker: it re-runs the start against the
    // already-selected price.
    case 'error':
      return (
        <ErrorStep
          message={error ?? t('checkout.errorGeneric')}
          onRetry={() => {
            setError(null);
            if (switchedFromUpgrade.current) {
              void handleSwitchIdentity();
              return;
            }
            if (!canGoBackToPicker && selectedProduct && selectedPrice) {
              if (initialTrial) {
                void handleStartTrial(selectedProduct, selectedPrice);
                return;
              }
              void handleSelectPrice(selectedProduct, selectedPrice, selectedAmountCents ?? undefined);
              return;
            }
            setStep('idle');
          }}
          onDismiss={handleDismiss}
        />
      );

    // Local origin whose hostname resolved no tenant: the developer guidance
    // surface, not a visitor-facing error.
    case 'localdev':
      return (
        <LocalDevStep
          host={window.location.hostname}
          port={window.location.port}
          onDismiss={handleDismiss}
        />
      );

    default:
      return null;
  }
}
